import { useCallback, useEffect, useRef, useState } from "react";
import { TabsList } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface ScrollableTabsListProps {
  children: React.ReactNode;
  className?: string;
}

// Space kept beside the active tab when it is scrolled into view.
const ACTIVE_TAB_MARGIN = 8;

export function ScrollableTabsList({ children, className }: ScrollableTabsListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollIndicators = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  const scrollActiveTabIntoView = useCallback(() => {
    const el = scrollRef.current;
    const active = el?.querySelector<HTMLElement>('[role="tab"][data-state="active"]');
    if (!el || !active) return;
    const start = active.offsetLeft;
    const end = start + active.offsetWidth;
    if (start < el.scrollLeft) {
      el.scrollTo({ left: start - ACTIVE_TAB_MARGIN });
    } else if (end > el.scrollLeft + el.clientWidth) {
      el.scrollTo({ left: end - el.clientWidth + ACTIVE_TAB_MARGIN });
    }
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollIndicators();
    scrollActiveTabIntoView();
    el.addEventListener("scroll", updateScrollIndicators, { passive: true });
    const resizeObserver = new ResizeObserver(() => {
      updateScrollIndicators();
      scrollActiveTabIntoView();
    });
    resizeObserver.observe(el);
    // A tab switch (click, keyboard, URL change) flips data-state on the triggers.
    const mutationObserver = new MutationObserver(scrollActiveTabIntoView);
    mutationObserver.observe(el, { subtree: true, attributes: true, attributeFilter: ["data-state"] });
    return () => {
      el.removeEventListener("scroll", updateScrollIndicators);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [updateScrollIndicators, scrollActiveTabIntoView]);

  // The arrows sit beside the scroll area, not on top of it, so they never cover a tab label.
  return (
    <div className={cn("flex h-10 items-stretch rounded-md bg-muted", className)}>
      {canScrollLeft && (
        // Pointer-only affordance: keyboard users scroll by moving between tabs.
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => scrollRef.current?.scrollBy({ left: -200, behavior: "smooth" })}
          className="flex w-8 flex-none items-center justify-center rounded-l-md text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      <TabsList
        ref={scrollRef}
        className={cn(
          // justify-start: a centred row that overflows spills past the left edge, where it cannot be scrolled to.
          "no-scrollbar relative flex h-10 min-w-0 flex-1 items-center justify-start overflow-x-auto bg-transparent",
          "[&>*]:min-w-fit [&>*]:flex-1 [&>*]:basis-0"
        )}
      >
        {children}
      </TabsList>
      {canScrollRight && (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => scrollRef.current?.scrollBy({ left: 200, behavior: "smooth" })}
          className="flex w-8 flex-none items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
